import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import CommentApi from 'app/modules/comments/api';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import {
  CommentAction,
  CommentAttachmentType,
} from 'app/modules/comments/types';
import { Editor } from '@tiptap/core';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { useTranslation } from 'react-i18next';

import { CommentConvertor } from 'app/modules/comments/utils/commentConvertor';
import { FileAttachmentsExtensionStorage } from 'app/modules/textEditor/utils/getTextEditorExtenstions';

export type UpdateCommentParams = {
  comment_id: number;
  text: string;
  action: CommentAction | null;
  postId: number;
  attachments?: {
    type: CommentAttachmentType;
    medium_id: string;
  }[];
};

type UseUpdateCommentProps = {
  editorRef: Editor | undefined;
  setEditorRef: (editor: Editor) => void;
};

export function useUpdateComment({
  editorRef,
  setEditorRef,
}: UseUpdateCommentProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { postId, commentAction, activeContent, resetCommentAction } =
    usePostCommunicationContext();

  const mutation = useMutation(
    async (values: UpdateCommentParams) => {
      return CommentApi.update({
        ...values,
      });
    },
    {
      onSuccess: async (response, { action }) => {
        try {
          toast(
            t(
              `workflowStatusMovedTo.${CommentConvertor.commentActionToStatus(
                action,
              )}`,
            ),
            'success',
          );
        } catch (error) {}

        await queryClient.refetchQueries(
          queryKey.postCommentsThread({
            postId,
            thread: activeContent,
          }),
        );
        queryClient.invalidateQueries(queryKey.postDetail(postId));
        toast(t('commentActions.edit.success'));
        onCommentUpdated();
      },
      onError(e: ApiClientError) {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  function onCommentUpdated() {
    editorRef?.commands.clearContent();
    resetCommentAction();
  }
  function onSubmit(comment_id: number) {
    if (editorRef) {
      const text = editorRef.getText();

      if (!text.trim()) {
        toast(t('validation.addCommentText'), 'warning');
        return;
      }

      const uploadedAttachments = (
        editorRef.storage.fileAttachments as FileAttachmentsExtensionStorage
      ).getFiles();

      mutation.mutate({
        comment_id,
        attachments: uploadedAttachments.map((attachment) => ({
          type: 'medium',
          medium_id: `${attachment.file?.medium_id ?? attachment.id}`,
        })),
        postId,
        text,
        action: commentAction,
      });
    }
  }

  return {
    editorRef,
    setEditorRef,
    onSubmit,
    isLoading: mutation.isLoading,
  };
}
